/* WorkflowNode — one positioned, draggable node of the workflow flow
 * graph. Standard nodes reuse the journey-graph chrome (icon chip +
 * title + sub); decisions render centered inside the clipped hexagon;
 * on_error markers pin to the top-right edge. Pure presentational — all
 * state (drag offsets, hover, tooltip) lives in WorkflowPipeline. */
import React from 'react';
import {
    Flag,
    GitBranch,
    HelpCircle,
    OctagonX,
    Play,
    Undo2,
    Wand2,
    Workflow as WorkflowIcon,
    Zap
} from 'lucide-react';
import type {
    WNode,
    WNodeKind
} from './workflowGraphTypes';
import './visuals.css';

const KIND_ICON: Record<WNodeKind, React.ComponentType<any>> = {
    start: Play,
    end: Flag,
    call: Zap,
    subflow: WorkflowIcon,
    transform: Wand2,
    decision: GitBranch,
    comp: Undo2,
    unknown: HelpCircle
};

interface Props {
    n: WNode;
    hl: boolean;
    onDown: (ev: React.PointerEvent<HTMLDivElement>) => void;
    onMove: (ev: React.PointerEvent<HTMLDivElement>) => void;
    onUp: () => void;
    onEnter: (ev: React.MouseEvent) => void;
    onLeave: () => void;
}

export default function WorkflowNode({
    n,
    hl,
    onDown,
    onMove,
    onUp,
    onEnter,
    onLeave
}: Props) {
    const Icon = KIND_ICON[n.kind];
    return (
        <div
            className={'aps-viz-node aps-wfg-node-' + n.kind + (hl ? ' hl' : '')}
            style={{
                left: n.x,
                top: n.y,
                width: n.w,
                height: n.h
            }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            {n.kind === 'decision' ? (
                <span className="aps-wfg-dec-inner">
                    <span className="aps-viz-node-title">{n.title}</span>
                    <span className="aps-viz-node-sub">
                        <Icon size={10} />
                        {n.sub}
                    </span>
                </span>
            ) : (
                <>
                    <span className="aps-viz-node-icon"><Icon size={14} /></span>
                    <span className="aps-viz-node-text">
                        <span className="aps-viz-node-title" style={{
                            display: 'block'
                        }}>
                            {n.title}
                        </span>
                        <span className="aps-viz-node-sub" style={{
                            display: 'block'
                        }}>
                            {n.sub}
                        </span>
                    </span>
                </>
            )}
            {n.marker === 'stop' && (
                <span className="aps-wfg-mark stop">
                    <OctagonX size={10} />
                    stop
                </span>
            )}
            {n.marker === 'skip' && (
                <span className="aps-wfg-mark skip">
                    skips on failure
                </span>
            )}
        </div>
    );
}
